/** RFC 0345 3g: a comments document is made by its subject's owner when the
 * subject is made, and opened to readers one person at a time. A viewer or a
 * Can comment guest gets write on comments documents only, never the page.
 */
import type { AnyDocumentId } from '@automerge/automerge-repo/slim'
import {
  Access,
  type DocumentId,
  docIdFromAutomergeUrl,
  GroupId,
  type Identifier,
  type Keyhive,
  MemberedId,
} from '@automerge/automerge-repo-keyhive'
import type { CommentsDoc } from '../model/comments/types'
import { issuerGroups } from './agent-grants'
import {
  admitToHistory,
  hex,
  type InviteLevel,
  type InviteTarget,
} from './invite'
import type { createSharingRepo } from './repo'

type Sharing = Awaited<ReturnType<typeof createSharingRepo>>

/** People who read the project and no more: its group's direct Read members.
 * Editors and owners already write its comments through the group. */
export async function projectReaders(
  hive: Keyhive,
  group: GroupId,
): Promise<Identifier[]> {
  const members = (await (await hive.getGroup(group))?.members()) ?? []
  // An issuer group holds agent keys, not a reader (3e).
  const issuers = new Set((await issuerGroups(hive, group)).map((g) => g.group))
  return members
    .filter(
      (c) =>
        c.who.isGroup &&
        c.can.equals(Access.read()) &&
        !issuers.has(hex(c.who.id.toBytes())),
    )
    .map((c) => c.who.id as Identifier)
}

/** Give every current project reader write on a new comments document. */
export async function grantReadersComments(
  hive: Keyhive,
  group: GroupId,
  comments: DocumentId,
): Promise<void> {
  for (const reader of await projectReaders(hive, group))
    await hive.addMember(
      reader,
      MemberedId.document(comments),
      Access.edit(),
      [],
    )
}

/** The comments document a subject links, when both directions agree: the
 * subject's commentsId names it and it names the subject back. Throws why
 * not, so a repointed commentsId never sends a grant to an unrelated doc. */
export async function boundComments(sharing: Sharing, subjectId: string) {
  const subject = await sharing.repo.find<{ commentsId?: string }>(
    subjectId as AnyDocumentId,
  )
  const commentsId = String(subject.doc()?.commentsId ?? '')
  if (!commentsId) throw new Error('This has no comments document to share')
  const comments = await sharing.repo.find<CommentsDoc>(
    commentsId as AnyDocumentId,
  )
  if (String(comments.doc()?.subjectId ?? '') !== subjectId)
    throw new Error('Its comments document belongs to something else')
  return { comments, id: docIdFromAutomergeUrl(comments.url) }
}

/** Upgrade a pre-S9 page only on its owner's device. The signed page owner
 * determines comments ownership; editable project metadata is not authority. */
export async function provisionGuestComments(
  sharing: Sharing,
  subjectId: string,
) {
  const hive = sharing.hive.keyhive
  const subject = await sharing.repo.find<{ commentsId?: string }>(
    subjectId as AnyDocumentId,
  )
  const id = docIdFromAutomergeUrl(subject.url)
  const own = await hive.bestAccessForDoc(hive.id.toIdentifier(), id)
  if (!own?.atLeast(Access.admin()))
    throw new Error('Only an owner of this document can share its comments')
  if (subject.doc().commentsId) return guestCommentsGrant(sharing, subjectId)
  const owners = (await (await hive.getDocument(id))?.members())?.filter(
    (c) => c.who.isGroup && c.can.equals(Access.admin()),
  )
  if (owners?.length !== 1)
    throw new Error('Document has no unambiguous signed ownership group')
  const group = new GroupId(owners[0].who.id.toBytes())
  const comments = await sharing.createOwnedDocument<CommentsDoc>(group, {
    subjectId,
    comments: {},
  })
  await grantReadersComments(hive, group, docIdFromAutomergeUrl(comments.url))
  // Another owner may have supplied a binding while creation was in flight.
  // Never replace it, and never silently repair a substituted binding.
  subject.change((doc) => {
    if (!doc.commentsId) doc.commentsId = comments.documentId
  })
  await sharing.repo.flush()
  await sharing.persistIdentity()
  return guestCommentsGrant(sharing, subjectId)
}

/** A document guest's grant on that one comments document, checked before
 * anything is granted. Only an owner of the comments may hand them on. */
export async function guestCommentsGrant(sharing: Sharing, subjectId: string) {
  const bound = await boundComments(sharing, subjectId)
  const hive = sharing.hive.keyhive
  const own = await hive.bestAccessForDoc(hive.id.toIdentifier(), bound.id)
  if (!own?.atLeast(Access.admin()))
    throw new Error('Only an owner of this document can share its comments')
  return bound
}

/** What a newly admitted person may write in comments, by how they got in.
 * Returns the comments documents granted, for their history, and the ones
 * refused with why. A guest's own check runs before settle grants the page. */
export async function grantComments(
  sharing: Sharing,
  options: { target: InviteTarget; level: InviteLevel },
  who: Identifier,
): Promise<{ granted: string[]; refused: string[] }> {
  const hive = sharing.hive.keyhive
  const granted: string[] = []
  const refused: string[] = []
  const grant = async (subjectId: string, access: Access) => {
    try {
      const bound = await boundComments(sharing, subjectId)
      await hive.addMember(who, MemberedId.document(bound.id), access, [])
      granted.push(bound.comments.documentId)
    } catch (error) {
      refused.push(`${subjectId}: ${(error as Error).message}`)
    }
  }
  const { target, level } = options
  if (target.kind === 'document') {
    await grant(
      target.documentId,
      level === 'read' ? Access.read() : Access.edit(),
    )
  } else if (level === 'read') {
    for (const subjectId of await commentSubjects(sharing, target))
      await grant(subjectId, Access.edit())
  }
  return { granted, refused }
}

/** The subjects under a project or session target that have a comments
 * document: the target itself and, for a project, each of its pages. A page
 * made before S9 has none, and there is nothing to open. Any editor can write
 * the catalog, so a page counts only when the project group signs on it. */
export async function commentSubjects(
  sharing: Sharing,
  target: InviteTarget,
): Promise<string[]> {
  const root = await sharing.repo.find<{
    documents?: Record<string, unknown>
    commentsId?: string
  }>(target.documentId as AnyDocumentId)
  const subjects = root.doc()?.commentsId ? [target.documentId] : []
  if (target.kind !== 'project') return subjects
  const hive = sharing.hive.keyhive
  for (const id of Object.keys(root.doc()?.documents ?? {})) {
    const page = await sharing.repo.find<{ commentsId?: string }>(
      id as AnyDocumentId,
    )
    if (!page.doc()?.commentsId) continue
    const members = await (
      await hive.getDocument(docIdFromAutomergeUrl(page.url))
    )?.members()
    const underTarget = members?.some(
      (c) => c.who.isGroup && hex(c.who.id.toBytes()) === target.membered.id,
    )
    if (underTarget) subjects.push(id)
  }
  return subjects
}

/** The bound comments documents under a project or session target, for an
 * editor's history: they write them through the group and need no grant. */
export async function boundCommentsUnder(
  sharing: Sharing,
  target: InviteTarget,
): Promise<string[]> {
  const out: string[] = []
  for (const subjectId of await commentSubjects(sharing, target)) {
    const bound = await boundComments(sharing, subjectId).catch(() => null)
    if (bound) out.push(bound.comments.documentId)
  }
  return out
}

/** RFC 0345 3g, 3j: taking a reader or guest off a target takes back the
 * comments they were given for it, and rotates each one's key so they read
 * nothing written after. RFC 0345 S12 calls this beside the group revoke. */
export async function revokeComments(
  sharing: Sharing,
  target: InviteTarget,
  who: Identifier,
): Promise<string[]> {
  const hive = sharing.hive.keyhive
  const subjects =
    target.kind === 'document'
      ? [target.documentId]
      : await commentSubjects(sharing, target)
  const revoked: string[] = []
  for (const subjectId of subjects) {
    const bound = await boundComments(sharing, subjectId).catch(() => null)
    if (!bound) continue
    const held = (await (await hive.getDocument(bound.id))?.members())?.some(
      (c) => c.who.id.toBytes().every((b, i) => b === who.toBytes()[i]),
    )
    if (!held) continue
    await hive.revokeMember(who, true, MemberedId.document(bound.id))
    revoked.push(bound.comments.documentId)
  }
  for (const id of revoked) await admitToHistory(sharing, id)
  return revoked
}
